import React, { useState, useRef } from 'react';
import { IoCloseOutline } from 'react-icons/io5';
import { MdOutlineDragIndicator } from "react-icons/md";

interface FilterListProps {
  theme?: any;
  closeModal: () => void;
  handleColumnSelector: (columns: any[]) => void;
  columns: any[];
}

const ColumnSelector: React.FC<FilterListProps> = ({ theme, closeModal, handleColumnSelector, columns }) => {
  const [columnList, setColumnList] = useState(columns);
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const selectorStyle: React.CSSProperties = {
    position: 'relative',
    backgroundColor: theme.table.style.backgroundColor,
    padding: '20px',
    borderRadius: '8px',
    overflowY: 'auto',
    overflowX: 'hidden',
  };

  const wrapperStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2
  };

  const closeButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: '10px',
    right: '10px',
    cursor: 'pointer',
  };

  const checkboxStyle: React.CSSProperties = {
    accentColor: theme.expanderButton.style.color,
  };

  const getItemStyle = (index: number): React.CSSProperties => ({
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    userSelect: 'none',
    padding: 7,
    margin: '0 0 8px 0',
    color: 'black',
    borderRadius: '8px',
    border: '1px solid #E2E8F0',
    cursor: 'move',
    background: isDragging && dragOverItem.current === index ? 'lightblue' : 'white',
    opacity: isDragging && dragItem.current === index ? 0.5 : 1,
    transform: isDragging && dragItem.current === index ? 'scale(1.05)' : 'scale(1)',
    transition: 'transform 0.2s, opacity 0.2s, background-color 0.2s',
  });

  const handleDragStart = (e: React.DragEvent, index: number) => {
    dragItem.current = index;
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.innerHTML);
    const dragImage = e.currentTarget as HTMLElement;
    e.dataTransfer.setDragImage(dragImage, 20, 20);
  };

  const handleDragEnter = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    dragOverItem.current = index;
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnd = () => {
    if (dragItem.current !== null && dragOverItem.current !== null) {
      const newColumnList = [...columnList];
      const draggedItemContent = newColumnList[dragItem.current];
      newColumnList.splice(dragItem.current, 1);
      newColumnList.splice(dragOverItem.current, 0, draggedItemContent);
      setColumnList(newColumnList);
    }
    dragItem.current = null;
    dragOverItem.current = null;
    setIsDragging(false);
  };

  const handleCheckboxChange = (index: number) => {
    const newColumnList = columnList.map((column, i) => {
      if (i === index) {
        return { ...column, isHidden: !column.isHidden };
      }
      return column;
    });
    setColumnList(newColumnList);
  };

  return (
    <div id="selector-wrapper" style={wrapperStyle}>
      <div style={selectorStyle} id="columnSelector">
        <div style={closeButtonStyle} onClick={closeModal}>
          <IoCloseOutline size={24} color={theme.expanderButton.style.color} />
        </div>
        <h3 style={{ fontWeight: 'bold' }}>Columns</h3>
        <div style={{ padding: 4, width: 250 }}>
          {columnList.map((column, index) => (
            <div
              key={column.identifier}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnter={(e) => handleDragEnter(e, index)}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              style={getItemStyle(index)}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <input
                  type="checkbox"
                  onChange={() => handleCheckboxChange(index)}
                  checked={!column.isHidden}
                  style={checkboxStyle}
                />
                {column.name}
              </div>
              <MdOutlineDragIndicator />
            </div>
          ))}
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: '20px'
        }}>
          <button
            onClick={() => {
              handleColumnSelector(columnList);
              closeModal();
            }}
            style={{
              border: '1px solid',
              backgroundColor: theme.expanderButton.style.color,
              width: '30%',
              borderRadius: '5px',
              cursor: 'pointer',
              color: '#fff',
              fontWeight: 'bold',
              padding: '5px 0px'
            }}
          >
            Save
          </button>
          <button
            onClick={closeModal}
            style={{
              border: '1px solid',
              borderColor: theme.expanderButton.style.color,
              backgroundColor: theme.table.style.backgroundColor,
              width: '30%',
              borderRadius: '5px',
              cursor: 'pointer',
              color: theme.expanderButton.style.color,
              fontWeight: 'bold',
              padding: '5px 0px'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ColumnSelector;